// frontend/src/config/api.ts
// Centralized API configuration to handle environment variables correctly

const RENDER_API_BASE = 'https://teakonn-backend.onrender.com';
const OLD_RAILWAY_HOST = 'teakonn-production.up.railway.app';

function replaceOldBackend(url: string): string {
  return url.includes(OLD_RAILWAY_HOST) ? RENDER_API_BASE : url;
}

function normalizeBase(url?: string): string {
  if (!url) return '';
  let u = url.trim().replace(/\/$/, '');
  u = replaceOldBackend(u);
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith('//')) {
    try {
      return (typeof window !== 'undefined' ? window.location.protocol : 'https:') + u;
    } catch {
      return 'https:' + u;
    }
  }
  if (u.startsWith('/')) {
    // If value is like "/host.tld" and contains a dot in first segment,
    // treat it as protocol-relative and convert to "https://host.tld".
    const afterSlash = u.slice(1);
    const firstSegment = afterSlash.split('/')[0] || '';
    if (!u.startsWith('//') && firstSegment.includes('.')) {
      try {
        const proto = typeof window !== 'undefined' ? window.location.protocol : 'https:';
        return `${proto}//${afterSlash}`;
      } catch {
        return 'https://' + afterSlash;
      }
    }
    try {
      return (typeof window !== 'undefined' ? window.location.origin : '') + u;
    } catch {
      return u;
    }
  }
  const isProd = (import.meta as any)?.env?.MODE === 'production';
  return `${isProd ? 'https://' : 'http://'}${u}`;
}

function runtimeAPIURL(): string {
  try {
    const runtime =
      (typeof window !== 'undefined' && (window as any).__API_URL) ||
      (typeof window !== 'undefined' && localStorage.getItem('API_URL')) ||
      '';
    const normalized = String(runtime || '').trim();
    if (typeof window !== 'undefined' && normalized.includes(OLD_RAILWAY_HOST)) {
      localStorage.removeItem('API_URL');
    }
    return replaceOldBackend(normalized);
  } catch {
    return '';
  }
}

const getBaseURL = (): string => {
  const runtimeURL = runtimeAPIURL();
  if (runtimeURL) return normalizeBase(runtimeURL);

  const envURL = import.meta.env.VITE_API_URL;
  if (envURL) return normalizeBase(envURL);

  // In production builds, prefer an explicit backend URL if no env var is set.
  // Falling back to frontend origin can incorrectly bind the app to the hosting
  // domain rather than a separate API backend.
  try {
    if (import.meta.env.MODE === 'production') {
      return RENDER_API_BASE;
    }
  } catch (e) {}

  // Default for local development
  return 'http://localhost:5000';
};

export const BASE_URL = getBaseURL();
export const API_URL = `${BASE_URL}/api`;

// Export for backward compatibility
export const API = BASE_URL;

// One-time runtime log to verify resolved API base in the deployed app
try {
  if (typeof window !== 'undefined' && !(window as any).__API_BASE_LOGGED) {
    (window as any).__API_BASE_LOGGED = true;
    // Keep it concise but informative for debugging
    console.log('[API] Resolved BASE_URL:', BASE_URL, '| API_URL:', API_URL);
  }
} catch {}
